import React, { useEffect, useState } from 'react';
import { get } from '../utils/api';

const MONTHS = ['Янв','Фев','Мар','Апр','Май','Июн','Июл','Авг','Сен','Окт','Ноя','Дек'];
const fmt = n => new Intl.NumberFormat('ru-RU').format(Math.round(n||0));

const METHOD_CODE = {
  'Наличные':          { code: 'Н', color: '#f0a500' },
  'Расчётный счёт':{ code: 'Б', color: '#4f8ef7' },
  'На карту':          { code: 'К', color: '#9b6ef7' },
};

// Закреплённые слева колонки отчёта: студия + арендатор
const STUDIO_COL_W = 70;
const stickyStudio = { position:'sticky', left:0, zIndex:2, width:STUDIO_COL_W, minWidth:STUDIO_COL_W, maxWidth:STUDIO_COL_W, boxSizing:'border-box' };
const stickyTenant = { position:'sticky', left:STUDIO_COL_W, zIndex:2, minWidth:180, boxShadow:'1px 0 0 var(--border)' };
const stickyBg = tint => tint
  ? { background:'var(--card)', backgroundImage:`linear-gradient(${tint}, ${tint})` }
  : { background:'var(--card)' };

export default function PaymentsReportPage() {
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [showArchive, setShowArchive] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const d = await get(`/reports/payments?year=${year}`); setReport(d); }
    catch(e) {}
    finally { setLoading(false); }
  };
  useEffect(()=>{ load(); },[year]);

  const contracts = report?.contracts || [];
  const active = contracts.filter(c=>c.status==='active');
  const archived = contracts.filter(c=>c.status!=='active');
  const displayed = showArchive ? [...active, ...archived] : active;

  const monthTotal = (m) => {
    let sum = 0;
    contracts.forEach(c => { const mo = c.months[m]; if (mo?.paid > 0) sum += mo.paid; });
    return sum;
  };

  const cellStyle = {
    padding: '3px 4px',
    textAlign: 'right',
    fontSize: 11,
    whiteSpace: 'nowrap',
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Отчёт по оплатам <span style={{color:'var(--text3)',fontWeight:400}}>{year}</span></h1>
        <div style={{display:'flex',gap:10,alignItems:'center'}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>setYear(y=>y-1)}>← {year-1}</button>
          <strong style={{fontSize:16}}>{year}</strong>
          <button className="btn btn-ghost btn-sm" onClick={()=>setYear(y=>y+1)}>{year+1} →</button>
          <button
            className={`btn btn-sm ${showArchive ? 'btn-primary' : 'btn-secondary'}`}
            onClick={()=>setShowArchive(v=>!v)}
          >
            {showArchive ? '✕ Скрыть архив' : '🗄 Архив'}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div style={{display:'flex',gap:16,marginBottom:16,flexWrap:'wrap',fontSize:12,color:'var(--text3)'}}>
        {Object.entries(METHOD_CODE).map(([label, {code, color}]) => (
          <div key={code} style={{display:'flex',alignItems:'center',gap:5}}>
            <span style={{background:color,color:'#fff',borderRadius:3,padding:'1px 5px',fontWeight:700,fontSize:10}}>{code}</span>
            {label}
          </div>
        ))}
      </div>

      {loading && <p style={{color:'var(--text3)'}}>Загрузка...</p>}

      {!loading && report && (
        <div className="card" style={{padding:0,overflow:'hidden'}}>
          {/* Студия и арендатор закреплены слева — месяцы скроллятся под ними по горизонтали.
              У закреплённых ячеек непрозрачный фон (тон строки накладывается градиентом поверх --card),
              иначе месяцы просвечивают при прокрутке. */}
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--bg3)',borderBottom:'1px solid var(--border)'}}>
                  <th style={{...stickyStudio,background:'var(--bg3)',padding:'6px 10px',textAlign:'left',fontWeight:600,fontSize:11,color:'var(--text3)',whiteSpace:'nowrap'}}>СТУДИЯ</th>
                  <th style={{...stickyTenant,background:'var(--bg3)',padding:'6px 10px',textAlign:'left',fontWeight:600,fontSize:11,color:'var(--text3)'}}>АРЕНДАТОР</th>
                  <th style={{padding:'6px 10px',textAlign:'right',fontWeight:600,fontSize:11,color:'var(--text3)',minWidth:80}}>СТАВКА</th>
                  {MONTHS.map((m,i)=>(
                    <th key={i} style={{padding:'6px 4px',textAlign:'right',fontWeight:600,fontSize:11,color:'var(--text3)',minWidth:72}}>{m}</th>
                  ))}
                </tr>
                {/* Totals row */}
                <tr style={{background:'rgba(79,142,247,0.08)',borderBottom:'2px solid var(--border)'}}>
                  <td colSpan={2} style={{...stickyStudio,width:undefined,maxWidth:undefined,boxShadow:'1px 0 0 var(--border)',...stickyBg('rgba(79,142,247,0.08)'),padding:'4px 10px',fontSize:11,color:'var(--text3)',fontWeight:600,whiteSpace:'nowrap'}}>ИТОГО ПО МЕСЯЦУ</td>
                  <td />
                  {MONTHS.map((_,i)=>{
                    const total = monthTotal(i+1);
                    return (
                      <td key={i} style={{...cellStyle, padding:'4px 4px', fontWeight:700, color: total>0 ? 'var(--accent)' : 'var(--text3)'}}>
                        {total > 0 ? fmt(total) : '—'}
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {displayed.length===0 && (
                  <tr><td colSpan={15} style={{textAlign:'center',color:'var(--text3)',padding:40}}>Нет договоров</td></tr>
                )}
                {displayed.map((c,idx)=>(
                  <tr key={c.id} style={{
                    borderBottom:'1px solid var(--border)',
                    opacity: c.status!=='active' ? 0.55 : 1,
                    background: idx%2===0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                  }}>
                    <td style={{...stickyStudio,...stickyBg(idx%2===0 ? null : 'rgba(255,255,255,0.02)'),padding:'3px 10px',fontWeight:700,fontSize:12,whiteSpace:'nowrap'}}>№{c.studio_number}</td>
                    <td style={{...stickyTenant,...stickyBg(idx%2===0 ? null : 'rgba(255,255,255,0.02)'),padding:'3px 10px',fontSize:12,whiteSpace:'nowrap'}}>
                      {c.name}
                      {c.status!=='active' && <span style={{marginLeft:6,fontSize:10,color:'var(--text3)'}}>({c.status==='closed'?'закрыт':'расторгнут'})</span>}
                    </td>
                    <td style={{...cellStyle,padding:'3px 10px',fontWeight:600,color:'var(--text2)'}}>{fmt(c.monthly_rate)}</td>
                    {MONTHS.map((_,i)=>{
                      const m = c.months[i+1];
                      if (!m || m.status==='na') {
                        return <td key={i} style={{...cellStyle,color:'var(--text3)'}}>·</td>;
                      }
                      if (m.paid > 0) {
                        const mc = METHOD_CODE[m.method] || { code: '?', color: 'var(--text3)' };
                        return (
                          <td key={i} style={cellStyle}>
                            <span
                              style={{display:'inline-flex',alignItems:'center',gap:3,...(m.outOfPeriod ? {borderBottom:'1px dotted #f0a500',paddingBottom:1} : {})}}
                              title={m.outOfPeriod ? 'Платёж за месяц вне срока договора — продлите договор' : undefined}
                            >
                              <span style={{background:mc.color,color:'#fff',borderRadius:3,padding:'0px 3px',fontWeight:700,fontSize:9,lineHeight:'14px'}}>{mc.code}</span>
                              <span style={{color:'var(--text)',fontWeight:600}}>{fmt(m.paid)}</span>
                              {m.outOfPeriod && <span style={{color:'#f0a500',fontWeight:700}}>*</span>}
                            </span>
                          </td>
                        );
                      }
                      // unpaid
                      return <td key={i} style={{...cellStyle,color:'var(--red)',fontWeight:600}}>—</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
